import { combineSlices, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { z } from 'zod';
import { addBreadcrumb } from '~/utils/observability';
import { stringToJSONSchema } from '~/utils/schemas';
import { gracefulStorage } from '~/utils/storage';
import { crewClaimSlice, crewClaimStateSchema } from './slices/crewClaim';
import {
  draftClaimLineItemSlice,
  draftClaimLineItemStateSchema,
} from './slices/draftClaimLineItem';
import { orderSlice, orderStateSchema } from './slices/order';
import {
  registrationsSlice,
  registrationsStateSchema,
} from './slices/registration';
import { settingsSlice, settingsStateSchema } from './slices/settings';
import * as sharedActions from './slices/sharedActions';
import {
  shippingProtectionClaimSlice,
  shippingProtectionClaimStateSchema,
} from './slices/shippingProtectionClaim';
import { statusSlice, statusStateSchema } from './slices/status';

export const stateSchema = z.object({
  [statusSlice.reducerPath]: statusStateSchema,
  [settingsSlice.reducerPath]: settingsStateSchema,
  [orderSlice.reducerPath]: orderStateSchema,
  [registrationsSlice.reducerPath]: registrationsStateSchema,
  [crewClaimSlice.reducerPath]: crewClaimStateSchema,
  [draftClaimLineItemSlice.reducerPath]: draftClaimLineItemStateSchema,
  [shippingProtectionClaimSlice.reducerPath]:
    shippingProtectionClaimStateSchema,
});

export const createAppStore = (initialState?: AppState | null) => {
  const store = configureStore({
    preloadedState: initialState ?? undefined,
    reducer: combineSlices(
      statusSlice,
      settingsSlice,
      orderSlice,
      crewClaimSlice,
      draftClaimLineItemSlice,
      registrationsSlice,
      shippingProtectionClaimSlice,
    ),
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(Sentry.createReduxEnhancer()),
  });

  const appStore = {
    ...store,
    actions: {
      ...sharedActions,
      ...statusSlice.actions,
      ...orderSlice.actions,
      ...settingsSlice.actions,
      [registrationsSlice.reducerPath]: registrationsSlice.actions,
      [crewClaimSlice.reducerPath]: crewClaimSlice.actions,
      [draftClaimLineItemSlice.reducerPath]: draftClaimLineItemSlice.actions,
      [shippingProtectionClaimSlice.reducerPath]:
        shippingProtectionClaimSlice.actions,
    },
  };

  return appStore;
};

const storageDataSchema = z.object({
  expiresAt: z.number(),
  data: z.unknown(),
});
const storage = gracefulStorage('localStorage');
const key = 'corso-app';

const persistState = (store: ReturnType<typeof createAppStore>) => () => {
  const expiryDurationMs = 1000 * 60 * 60; // 1 hour
  const expiresAt = Date.now() + expiryDurationMs;
  storage.setItem(
    key,
    JSON.stringify({ expiresAt, data: store.getState() } satisfies z.infer<
      typeof storageDataSchema
    >),
  );
};

const retrieveState = () => {
  const value = storage.getItem(key);

  if (value === null) {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'AppStore',
      data: { key },
      message: 'No data found in localStorage',
    });
    return null;
  }

  const retrievalResult = stringToJSONSchema.safeParse(value);

  if (!retrievalResult.success) {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'AppStore',
      data: { error: retrievalResult.error, input: value },
      message: 'Failed to parse data from localStorage',
    });

    return null;
  }

  const persistedData = storageDataSchema.safeParse(retrievalResult.data);
  if (!persistedData.success) {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'AppStore',
      data: { error: persistedData.error, input: retrievalResult.data },
      message: 'Failed to validate persisted data from localStorage',
    });
    return null;
  }

  const { expiresAt, data } = persistedData.data;
  const parsed = stateSchema.safeParse(data);
  if (!parsed.success) {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'AppStore',
      data: { error: parsed.error, input: retrievalResult.data },
      message: 'Failed to validate data from localStorage',
    });
    return null;
  }

  const expired = expiresAt < Date.now();
  if (expired) {
    addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'AppStore',
      data: { expiresAt, input: retrievalResult.data },
      message: 'Persisted data from localStorage has expired',
    });
    storage.removeItem(key); // clean up expired data
  }

  return {
    ...parsed.data,
    status: expired ? ('expired' as const) : ('valid' as const),
  };
};

export const initializeAppStore = () => {
  const appStore = createAppStore(retrieveState());
  appStore.subscribe(persistState(appStore));
  return appStore;
};

export type AppState = z.infer<typeof stateSchema>;
export type AppStore = ReturnType<typeof createAppStore>;
